import { useEffect, useState } from 'react';
import {
  studioRecipesApi,
  type ConfluenceSearchHit,
  type ConfluenceSyncResponse,
} from '../../lib/studioRecipesApi';

interface Props {
  slug: string;
  /** Default search query (typically the studio's display name). */
  initialQuery: string;
  onClose: () => void;
  /** Called after a successful sync with the persisted recipe + page meta. */
  onSynced: (result: ConfluenceSyncResponse) => void;
}

/**
 * Modal for connecting a studio recipe to a Confluence page. Two states:
 *   1. Search: user types a query (defaults to the studio name), we hit
 *      `/confluence/search`, render up to 10 hits with title + excerpt.
 *   2. Confirm + sync: user picks a hit -> we POST to `/confluence/sync`,
 *      show progress (this can take 30s+ because of two AI calls), and on
 *      success bubble the result back up so the form can refresh.
 */
export function ConfluencePickerDialog({ slug, initialQuery, onClose, onSynced }: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<ConfluenceSearchHit[]>([]);
  const [syncingPageId, setSyncingPageId] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const runSearch = async (q: string) => {
    setSearching(true);
    setSearchError(null);
    try {
      const data = await studioRecipesApi.confluenceSearch(q);
      setResults(data);
    } catch (err) {
      setSearchError((err as Error).message);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Kick off an initial search with the studio name so the user usually
  // sees the right page on open without typing anything.
  useEffect(() => {
    if (initialQuery.trim().length >= 2) {
      void runSearch(initialQuery);
    }
  }, [initialQuery]);

  const onPick = async (hit: ConfluenceSearchHit) => {
    setSyncingPageId(hit.id);
    setSyncError(null);
    try {
      const result = await studioRecipesApi.confluenceSync(slug, hit.id);
      onSynced(result);
    } catch (err) {
      setSyncError((err as Error).message);
    } finally {
      setSyncingPageId(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Connect to Confluence</h2>
          <span className="spacer" />
          <button className="btn btn-ghost" onClick={onClose} disabled={!!syncingPageId}>
            Close
          </button>
        </div>

        <p className="muted small" style={{ marginTop: 0 }}>
          Search the Confluence space for this studio's wiki page. Picking a result triggers an
          AI-driven sync that drafts the recipe and a short brief; the brief is reused as
          studio context in every downstream AI call.
        </p>

        <div className="row" style={{ gap: 8 }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void runSearch(query);
            }}
            placeholder="Studio name (e.g. Octoplay)"
            style={{ flex: 1 }}
            autoFocus
            disabled={!!syncingPageId}
          />
          <button
            className="btn btn-primary"
            onClick={() => void runSearch(query)}
            disabled={searching || query.trim().length < 2 || !!syncingPageId}
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {searchError && (
          <div className="card" style={{ borderColor: 'var(--bad)', marginTop: 12 }}>
            {searchError}
          </div>
        )}

        <div className="flex-col" style={{ gap: 6, marginTop: 12 }}>
          {!searching && results.length === 0 && !searchError && (
            <p className="muted small" style={{ margin: 0 }}>
              {query.trim() ? 'No results. Try a different query.' : 'Type a query and press Search.'}
            </p>
          )}
          {results.map((hit) => {
            const busy = syncingPageId === hit.id;
            return (
              <div
                key={hit.id}
                className="card"
                style={{ padding: 10, opacity: syncingPageId && !busy ? 0.4 : 1 }}
              >
                <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <strong>{hit.title}</strong>
                      {hit.spaceKey && (
                        <span className="badge small mono">{hit.spaceKey}</span>
                      )}
                    </div>
                    {hit.excerpt && (
                      <p
                        className="muted small"
                        style={{
                          margin: '4px 0 0 0',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {hit.excerpt}
                      </p>
                    )}
                    <a
                      href={hit.url}
                      target="_blank"
                      rel="noreferrer"
                      className="muted small"
                      style={{ display: 'inline-block', marginTop: 4 }}
                    >
                      Open in Confluence ↗
                    </a>
                  </div>
                  <button
                    className="btn small btn-primary"
                    disabled={!!syncingPageId}
                    onClick={() => void onPick(hit)}
                  >
                    {busy ? 'Syncing (~30s)...' : 'Sync this page'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {syncError && (
          <div className="card" style={{ borderColor: 'var(--bad)', marginTop: 12 }}>
            <strong>Sync failed.</strong> {syncError}
          </div>
        )}
      </div>
    </div>
  );
}
